import {mapObject} from '@benchling/js-utils';
import React from 'react';
import _ from 'underscore';

import {LogLevel} from 'config-migration/migration-log/LogLevel';
import {MigrationError, MigrationObjectPlan} from 'config-migration/MigrationConstants';
import {OBJECT_TYPE_TO_DISPLAY_TEXT, ObjectType} from 'config-migration/types/ObjectType';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Section from 'ui-library/menus/Section';
import SectionHeader from 'ui-library/menus/SectionHeader';

import {ObjectAction, ObjectActionType} from '../../types/objectActions';
import {
  ImportPlanSectionRowWrapper,
  ObjectDisplayNameColumn,
} from '../import-plan-sections/ImportPlanSectionRow';
import {getActionDescription, getActionOption, getOptionValue} from '../ObjectActionDropdown';

export interface TemplatePlanRowProps {
  plan: MigrationObjectPlan;
  errors: Array<MigrationError>;
  onUpdatePlan: (plan: MigrationObjectPlan) => void;
  templateCollectionPlan: MigrationObjectPlan;
}

export default function TemplatePlanRow(props: TemplatePlanRowProps): JSX.Element {
  const {plan, errors} = props;
  const hasErrors = errors.filter(({level}) => level === LogLevel.ERROR).length > 0;
  const hasWarnings = errors.filter(({level}) => level === LogLevel.WARNING).length > 0;
  const icon = iconForObjectType(props.plan.objectType);
  const label = labelForObjectType(props.plan.objectType);
  return (
    <>
      <ImportPlanSectionRowWrapper
        plan={plan}
        hasErrors={hasErrors}
        hasWarnings={hasWarnings}
        key={plan.objectId}
      >
        <ObjectDisplayNameColumn
          hasErrors={hasErrors}
          hasWarnings={hasWarnings}
          rowIcon={icon}
          subLabel={label}
          plan={plan}
          errors={errors}
          setIsCollapsed={(input) => {}}
          isCollapsed={false}
          hasChildren={false}
          highImpactChangeCount={0}
        />
        <TemplateActionDropdownColumn
          plan={plan}
          onUpdatePlan={props.onUpdatePlan}
          templateCollectionPlan={props.templateCollectionPlan}
        />
      </ImportPlanSectionRowWrapper>
    </>
  );
}

function iconForObjectType(objectType: ObjectType): BenchlingIconName | undefined {
  switch (objectType) {
    case ObjectType.Template:
      return ModelTypeConstants.TEMPLATE;
    case ObjectType.Subtemplate:
      return ModelTypeConstants.SUBTEMPLATE;
    case ObjectType.Procedure:
      return ModelTypeConstants.PROCEDURE;
    default:
      return undefined;
  }
}

function labelForObjectType(objectType: ObjectType): string {
  switch (objectType) {
    case ObjectType.Template:
      return OBJECT_TYPE_TO_DISPLAY_TEXT[ObjectType.Template];
    case ObjectType.Subtemplate:
      return OBJECT_TYPE_TO_DISPLAY_TEXT[ObjectType.Subtemplate];
    case ObjectType.Procedure:
      // TODO(BNCH-109180): This should be more specific and depend on the procedure sub type.
      return OBJECT_TYPE_TO_DISPLAY_TEXT[ObjectType.Procedure];
    default:
      return '';
  }
}

function TemplateActionDropdownColumn(props: {
  plan: MigrationObjectPlan;
  onUpdatePlan: (plan: MigrationObjectPlan) => void;
  templateCollectionPlan: MigrationObjectPlan;
}): JSX.Element {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const onUpdateAction = (selectedAction: ObjectAction) => {
    props.onUpdatePlan({...props.plan, selectedAction});
  };
  return (
    <div className="simpleDivTable-cell">
      <SelectDropdown
        className="u-mini-spacing-right u-flex-grow"
        disabled={props.templateCollectionPlan.selectedAction.type === ObjectActionType.IGNORE}
        menu={
          <Menu
            height={Menu.HEIGHTS.MEDIUM}
            width={Menu.WIDTHS.FULL}
            value={getOptionValue(props.plan.selectedAction)}
            scrollable={true}
          >
            <Section>
              {getActionOption({type: ObjectActionType.IGNORE}, onUpdateAction)}
              {!props.plan.hasProvenanceUriMatch &&
                getActionOption({type: ObjectActionType.CREATE}, onUpdateAction)}
            </Section>
            {props.plan.initialAction.type === ObjectActionType.UPDATE && props.plan.hasProvenanceUriMatch
              ? getTemplateUpdateSectionForCollection(
                  props.plan.initialAction.target.parentIdentifier!.displayName,
                  [props.plan.initialAction],
                  onUpdateAction
                )
              : getTemplateUpdateOptions(props.plan, onUpdateAction, props.templateCollectionPlan)}
          </Menu>
        }
        displayName={getActionDescription(props.plan.selectedAction)}
      />
    </div>
  );
}

function getTemplateUpdateSectionForCollection(
  collectionName: string,
  actions: Array<ObjectAction>,
  onSelectAction: (selectedAction: ObjectAction) => void
): JSX.Element {
  return (
    <Section key={_.uniqueId('template-update-')}>
      <SectionHeader>
        <BenchlingIcon icon={ModelTypeConstants.TEMPLATE_COLLECTION} />
        <span className="u-tiny-spacing-left">{collectionName}</span>
      </SectionHeader>
      {actions.map((action) => getActionOption(action, onSelectAction))}
    </Section>
  );
}

function getTemplateUpdateOptions(
  plan: MigrationObjectPlan,
  onUpdatePlanAction: (selectedAction: ObjectAction) => void,
  templateCollectionPlan: MigrationObjectPlan
): Array<JSX.Element> {
  // Group the actions by collection, and figure out the name of each collection.
  const actionsByCollectionId = _.groupBy(
    plan.additionalActions,
    (action) => action.target.parentIdentifier!.apiIdentifier
  );
  const collectionApiIdentifierToName = mapObject(
    actionsByCollectionId,
    (plans) => plans[0].target.parentIdentifier!.displayName
  );

  // Figure out if the given template is being placed in an existing collection that is being updated.
  const updatingTemplateCollectionId =
    templateCollectionPlan.selectedAction.type === ObjectActionType.UPDATE &&
    templateCollectionPlan.selectedAction.target.apiIdentifier;

  // Create menu sections for each collection, sorted by collection name and excluding the collection being
  // updated, which we'll bubble up to the top.
  const nameSortedCollectionIds = _.sortBy(
    Object.keys(actionsByCollectionId).filter(
      (collectionId) => !updatingTemplateCollectionId || collectionId !== updatingTemplateCollectionId
    ),
    (collectionId) => collectionApiIdentifierToName[collectionId].toLowerCase()
  );

  const nameSortedCollectionSections = nameSortedCollectionIds.map((collectionId) =>
    getTemplateUpdateSectionForCollection(
      collectionApiIdentifierToName[collectionId],
      actionsByCollectionId[collectionId],
      onUpdatePlanAction
    )
  );

  if (updatingTemplateCollectionId && actionsByCollectionId[updatingTemplateCollectionId]) {
    return [
      getTemplateUpdateSectionForCollection(
        collectionApiIdentifierToName[updatingTemplateCollectionId],
        actionsByCollectionId[updatingTemplateCollectionId],
        onUpdatePlanAction
      ),
      ...nameSortedCollectionSections,
    ];
  }
  return nameSortedCollectionSections;
}
